import { useEffect, useState } from 'react';
import { cn } from '../../../lib/utils';

const SAMPLE_TEXT = 'Where ambitious brands create';

interface FontViewerProps {
	url: string;
	name: string;
	lightBg: boolean;
}

export function FontViewer({ url, name, lightBg }: FontViewerProps) {
	const [loaded, setLoaded] = useState(false);
	const [customText, setCustomText] = useState('');
	const fontFamily = `preview-font-${name.replace(/[^a-zA-Z0-9]/g, '-')}`;

	useEffect(() => {
		const font = new FontFace(fontFamily, `url(${url})`);
		font.load()
			.then((f) => {
				document.fonts.add(f);
				setLoaded(true);
			})
			.catch(() => setLoaded(true));

		return () => {
			document.fonts.forEach((f) => {
				if (f.family === fontFamily) document.fonts.delete(f);
			});
		};
	}, [url, fontFamily]);

	const displayText = customText || SAMPLE_TEXT;

	return (
		<div
			className={cn(
				'flex h-full flex-col items-center justify-center overflow-hidden p-8 transition-colors duration-200',
				lightBg ? 'bg-stripes-light' : 'bg-stripes-dark'
			)}
		>
			{!loaded ? (
				<div className="text-base text-text-4">Loading font…</div>
			) : (
				<div
					className="flex w-full max-w-[600px] flex-col items-center gap-6"
					style={{ fontFamily }}
				>
					{/* Large preview */}
					<div
						className={cn(
							'w-full text-center text-4xl leading-[1.2]',
							lightBg ? 'text-black' : 'text-text-1'
						)}
						style={{ fontFamily, wordBreak: 'break-word' }}
					>
						{displayText}
					</div>

					{/* Custom text input */}
					<input
						value={customText}
						onChange={(e) => setCustomText(e.target.value)}
						placeholder="Type to preview…"
						className={cn(
							'w-full max-w-[400px] border-b bg-transparent pb-2 text-center text-lg outline-none transition-colors',
							lightBg
								? 'border-black/10 text-black placeholder:text-black/30'
								: 'border-line-2 text-text-1 placeholder:text-text-4'
						)}
						style={{ fontFamily }}
					/>

					{/* Small meta line */}
					<div
						className={cn(
							'font-sans text-xs',
							lightBg ? 'text-black/30' : 'text-text-4'
						)}
					>
						{name}
					</div>
				</div>
			)}
		</div>
	);
}
